import * as React from "react";
import { Button, CircularProgress, Typography } from "@material-ui/core";
import { Dialog } from "../Dialog";
import { Mutation } from "../../generated/nest-graphql";
import { useMutation } from "@apollo/client";
import { useDispatch } from "../../contexts/snackbar-context";
import { showErrorAlert, showSuccessAlert } from "../../actions";
import * as Sentry from "@sentry/browser";
import { SEND_CHECKOUT_LINK_EVENT } from "../../graphql/mutations/sendCheckoutLink";
import { UpdateInvoiceDetailsFormValues } from "../Forms/UpdateInvoiceDetailsForm";
import { useInvoice } from "../../hooks/useInvoice";

export const CreateInvoiceDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  jobId: string;
  values: UpdateInvoiceDetailsFormValues;
  isFlorida: boolean;
}> = ({ open, onClose, jobId, values, isFlorida }) => {
  const dispatch = useDispatch();
  const [sendCheckoutLinkEvent, { loading }] = useMutation<Mutation>(SEND_CHECKOUT_LINK_EVENT);
  const handleSendCheckoutLink = async (invoiceId: string, onCompletedParams?: any) => {
    try {
      if (onCompletedParams?.sendCheckoutLink) {
        await sendCheckoutLinkEvent({
          variables: { invoiceId },
        });
      }
      showSuccessAlert(dispatch, "Invoice created");
      onClose();
    } catch (e) {
      showErrorAlert(dispatch, "Failed to create invoice");
      onClose();
      Sentry.captureException(e, {
        extra: { invoiceId },
      });
    }
  };
  const { createJobInvoice } = useInvoice(handleSendCheckoutLink);
  const onSubmit = async (sendCheckoutLink: boolean) => {
    await createJobInvoice(jobId, values, { sendCheckoutLink });
    onClose();
  };

  return (
    <Dialog title={"Send Checkout Link?"} open={open} onClose={onClose}>
      <div className="mb-8">
        <Typography gutterBottom>
          By hitting confirm, your customer will be texted a link to view and pay their invoice.
        </Typography>
      </div>
      <div className="flex justify-between mb-4">
        <Button
          onClick={isFlorida ? onClose : () => onSubmit(false)}
          size={"large"}
          color={"default"}
          variant={"outlined"}
        >
          {isFlorida ? "Cancel" : "Don't Send"}
        </Button>
        <Button
          disabled={loading}
          onClick={() => onSubmit(true)}
          size={"large"}
          color={"primary"}
          variant={"contained"}
        >
          {loading ? <CircularProgress /> : isFlorida ? "Create Invoice" : "Send"}
        </Button>
      </div>
    </Dialog>
  );
};
